<template>
  <Flyout
    v-on-clickaway="closeOverlay"
    :isOpen="supportOverlayOpen"
    @close="closeOverlay"
  >
    <template v-slot:title>
      <div>
        <HeadingMedium class="flyoutTitle">
          <Icon
            icon="support"
            :scale="1.25"
          />
          Support
        </HeadingMedium>
      </div>
    </template>

    <template v-slot:body>
      <ul>
        <li
          v-for="item in supportPanelItems"
          :key="item.title"
        >
          <div
            aria-hidden="true"
            class="iconWrapper"
          >
            <Icon
              v-if="typeof item.icon === 'string'"
              :icon="item.icon"
              :scale="2"
            />
            <CustomIcon
              v-else
              :scale="2"
            >
              <component :is="item.icon" />
            </CustomIcon>
          </div>

          <div class="textWrapper">
            <HeadingExtraSmall class="heading">
              {{ item.title }}
              <StatusBadge
                v-if="item.new"
                class="newBadge"
                status="warning"
                text="New"
              />
            </HeadingExtraSmall>
            <ParagraphBody>{{ item.text }}</ParagraphBody>
            <div>
              <Link
                class="link"
                :data-test-id="item.testId"
                :href="item.linkUrl"
                :isExternal="item.isLinkExternal"
                target="_blank"
                :text="item.linkText"
                @click.native="item.handleClick"
              />
            </div>
          </div>
        </li>
      </ul>
    </template>
  </Flyout>
</template>

<script>
import {
  CustomIcon,
  HeadingExtraSmall,
  HeadingMedium,
  Icon,
  Link,
  ParagraphBody,
  StatusBadge,
} from '@jumpcloud/ui-components';
import {
  eventLocations,
  eventProperties,
  events,
} from 'client/GrowthEngineering/utilities/GrowthConstants';
import { mapActions, mapState } from 'vuex';
import { directive as onClickaway } from 'vue-clickaway';
import EventHelper from 'client/GrowthEngineering/EventHelper';
import EventManager from 'client/GrowthEngineering/EventManager';
import Flyout from 'client/components/Flyout.vue';
import KbArticleUrls from 'client/util/Constants/KbArticleUrls';
import ModalEventBus from 'components/Modal/ModalEventBus';
import SubmitIdeaModal from 'client/Support/SubmitIdeaModal.vue';
import Util from 'client/util/Util';

const supportModule = 'SupportModule';

export default {
  name: 'SupportOverlay',
  components: {
    CustomIcon,
    Flyout,
    HeadingExtraSmall,
    HeadingMedium,
    Icon,
    Link,
    ParagraphBody,
    StatusBadge,
  },

  directives: {
    onClickaway,
  },

  computed: {
    ...mapState(supportModule, ['supportOverlayOpen']),

    supportPanelItems() {
      return [
        {
          testId: this.$testId('contactSupportLink'),
          title: 'Reach out to support',
          text: 'Having trouble? We would like to help.',
          icon: 'help',
          linkText: 'Create A Support Ticket',
          handleClick: (event) => {
            EventManager.triggerEvent(events.contactSupportClicked);

            // using Util.redirect() instead of $router.push() because of a bug
            // context: https://thejumpcloud.slack.com/archives/C035ZE4CWHH/p1668803699305649
            Util.redirect('/#/support/contact');
            this.closeOverlay();
            event.preventDefault();
          },
        },

        {
          testId: this.$testId('submitIdeaLink'),
          title: 'Let us know your ideas',
          text: 'Have ideas to improve JumpCloud? We would like to hear about them.',
          icon: 'ideaBot',
          linkText: 'Submit An Idea',
          handleClick: (event) => {
            ModalEventBus.open({
              component: SubmitIdeaModal,
              isLargeModal: true,
            });
            this.closeOverlay();
            event.preventDefault();
          },
        },

        {
          testId: this.$testId('casePortalLink'),
          title: 'Case Portal',
          text: 'These are ideas and support cases sent to us by your organization.',
          icon: 'casePortal',
          linkText: 'View Case Portal',
          handleClick: (event) => {
            // using Util.redirect() instead of $router.push() because of a bug
            // context: https://thejumpcloud.slack.com/archives/C035ZE4CWHH/p1668803699305649
            Util.redirect('/#/support/case-portal');
            this.closeOverlay();
            event.preventDefault();
          },
          new: true,
        },

        {
          testId: this.$testId('learnJumpcloudLink'),
          title: 'Learn JumpCloud',
          text: 'We have the resources to help you on your JumpCloud journey.',
          linkText: 'Go To Knowledge Base',
          linkUrl: KbArticleUrls.support,
          icon: 'resources',
          isLinkExternal: true,
        },

        {
          testId: this.$testId('contactProfessionalServicesLink'),
          title: 'Talk with Professional Services',
          text: 'Concierge-level service awaits!',
          linkText: 'Schedule Free Consultation',
          linkUrl: 'https://hello.jumpcloud.com/calendar/team/t/58',
          icon: 'mobile',
          handleClick: () => {
            EventHelper.triggerLinkClicked({
              [eventProperties.description]: 'Opens Calendly Link for Free Consultation',
              [eventProperties.location]: eventLocations.supportPage,
              [eventProperties.section]: 'Implementation Guide Card',
              [eventProperties.text]: 'Free Consultation',
            });
          },
          isLinkExternal: true,
        },
      ];
    },
  },

  methods: {
    ...mapActions(supportModule, ['setSupportOverlayOpen']),

    closeOverlay() {
      this.setSupportOverlayOpen(false);
      this.$emit('close');
    },
  },
};
</script>

<style scoped>
.flyoutTitle {
  align-items: center;
  display: flex;
  gap: 10px;
}

.heading {
  align-items: center;
  display: flex;
}

ul {
  list-style-type: none;
  padding: 0;
}

li {
  align-items: center;
  border-bottom: 1px solid var(--jcGrey200);
  display: flex;
  padding-bottom: var(--jc-size-1p75);
  padding-top: var(--jc-size-1p75);

  --jc-spacer: 0.5rem;
}

li .iconWrapper {
  align-items: center;
  display: flex;
  height: 100%;
  justify-content: center;
  min-width: 5.875rem;
}

.link {
  font-size: var(--jcBody);
}

.newBadge {
  margin-left: 1rem;

  --jc-spacer: 1rem;
}
</style>
